import { useDocumentTitle } from "@/lib/use-document-title";
import { Link, useSearchParams, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Mail, RotateCw, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { BackLink } from "@/components/BackLink";
import { customerUrl } from "@/lib/public-urls";

const COOLDOWN_SECONDS = 60;

export default function BekraftaMejl() {
  useDocumentTitle("Bekräfta din e-post — Lagamobilen Access");
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const [cooldown, setCooldown] = useState(COOLDOWN_SECONDS);
  const [resent, setResent] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (!email) return <Navigate to="/access/skapa-konto" replace />;

  async function resend() {
    setErr("");
    setBusy(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: customerUrl("/access/dashboard") },
    });
    setBusy(false);
    if (error) {
      if (error.message.toLowerCase().includes("already")) {
        setErr("Den här e-posten är redan bekräftad. Du kan logga in direkt.");
      } else {
        setErr(error.message);
      }
      return;
    }
    setResent(true);
    setCooldown(COOLDOWN_SECONDS);
    setTimeout(() => setResent(false), 4000);
  }

  const domain = email.split("@")[1]?.toLowerCase() || "";
  const inboxUrl =
    domain.includes("gmail") ? "https://mail.google.com" :
    domain.includes("outlook") || domain.includes("hotmail") || domain.includes("live") ? "https://outlook.live.com/mail" :
    domain.includes("yahoo") ? "https://mail.yahoo.com" :
    domain.includes("icloud") || domain.includes("me.com") ? "https://www.icloud.com/mail" :
    null;

  return (
    <div className="min-h-screen bg-[var(--color-soft)]">
      <main className="flex items-center justify-center px-6 sm:px-10 py-12 lg:py-16">
        <div className="w-full max-w-md">
          <div className="mb-6"><BackLink fallback="/access/skapa-konto" /></div>

          <div className="card-soft p-8 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center mb-5">
              <Mail size={26} className="text-[var(--color-primary)]" />
            </div>

            <h1 className="!text-2xl text-foreground mb-2">Kolla din inkorg</h1>
            <p className="text-sm text-muted-foreground mb-6">
              Vi har skickat ett bekräftelsemejl till
              <br />
              <span className="font-display font-semibold text-foreground">{email}</span>
            </p>

            <p className="text-xs text-muted-foreground mb-6 leading-relaxed">
              Klicka på länken i mejlet för att aktivera ditt medlemskap. Kolla skräpposten om du inte hittar det inom någon minut.
            </p>

            {inboxUrl && (
              <a href={inboxUrl} target="_blank" rel="noopener noreferrer"
                className="btn-pill w-full mb-3 inline-flex items-center justify-center gap-2">
                Öppna inkorgen
              </a>
            )}

            <button onClick={resend} disabled={busy || cooldown > 0}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full border border-border text-sm font-display font-semibold tracking-wide hover:bg-muted/50 transition disabled:opacity-50 disabled:cursor-not-allowed">
              {resent ? (
                <><CheckCircle2 size={16} className="text-[var(--color-success)]" /> Mejl skickat igen</>
              ) : busy ? (
                <>Skickar…</>
              ) : cooldown > 0 ? (
                <><RotateCw size={14} /> Skicka igen om {cooldown}s</>
              ) : (
                <><RotateCw size={14} /> Skicka mejlet igen</>
              )}
            </button>

            {err && <p className="text-destructive text-xs mt-3">{err}</p>}

            <div className="mt-7 pt-6 border-t border-border space-y-2 text-xs text-muted-foreground">
              <p>
                Fel adress?{" "}
                <Link to="/access/skapa-konto" className="text-primary hover:underline font-semibold">Börja om</Link>
              </p>
              <p>
                Har du redan bekräftat?{" "}
                <Link to="/access/login" className="text-primary hover:underline font-semibold">Logga in</Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
